<template>
  <div v-if="show" class="modal-overlay" @click.self="handleClose">
    <div class="modal-content">
      <div class="modal-header">
        <h2>Créer un avatar</h2>
        <button class="modal-close" @click="handleClose">×</button>
      </div>

      <form class="modal-body" @submit.prevent="handleSubmit">
        <p class="modal-description">
          Avatar du bloc <strong>{{ bloc?.object_name }}</strong>
        </p>

        <div class="form-group">
          <label for="avatar-color">Couleur de remplissage</label>
          <div class="color-row">
            <input
              id="avatar-color"
              v-model="color"
              type="color"
              class="color-picker"
            />
            <input
              v-model="color"
              type="text"
              class="color-input"
              pattern="^#[0-9A-Fa-f]{6}$"
              placeholder="#7AD06D"
            />
          </div>
        </div>

        <div class="preview-wrapper">
          <span class="preview-label">Aperçu</span>
          <div class="preview-chip">
            <span class="preview-dot" :style="{ backgroundColor: color }"></span>
            {{ color.toUpperCase() }}
          </div>
        </div>

        <div class="modal-footer">
          <button type="button" class="btn-cancel" @click="handleClose">Annuler</button>
          <button type="submit" class="btn-submit" :disabled="isSubmitDisabled">
            {{ loading ? 'Création...' : 'Créer avatar' }}
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import type { Bloc } from '@api/blocs.api';
import { useBlocsStore } from '@stores/blocs.store';

const props = defineProps<{
  show: boolean;
  bloc: Bloc | null;
}>();

const emit = defineEmits<{
  close: [];
  created: [bloc: Bloc];
}>();

const blocsStore = useBlocsStore();
const DEFAULT_AVATAR_COLOR = '#655e87';
const color = ref(DEFAULT_AVATAR_COLOR);
const loading = ref(false);
const HEX_COLOR_RE = /^#[0-9A-Fa-f]{6}$/;

const isSubmitDisabled = computed(() => {
  return loading.value || !props.bloc || !HEX_COLOR_RE.test(color.value.trim());
});

watch(
  () => props.show,
  (show) => {
    if (show) {
      color.value = props.bloc?.avatar_color ?? DEFAULT_AVATAR_COLOR;
    }
  },
  { immediate: true }
);

function handleClose(): void {
  if (loading.value) return;
  emit('close');
}

async function handleSubmit(): Promise<void> {
  if (!props.bloc || isSubmitDisabled.value) return;
  loading.value = true;
  try {
    const avatar = await blocsStore.createAvatar(props.bloc.id, color.value.trim());
    emit('created', avatar);
  } finally {
    loading.value = false;
  }
}
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-content {
  background-color: #2a2a2a;
  border: 1px solid #444;
  border-radius: 8px;
  width: 90%;
  max-width: 460px;
  overflow: hidden;
}

.modal-header {
  padding: 1rem 1.25rem;
  border-bottom: 1px solid #444;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.modal-header h2 {
  margin: 0;
  font-size: 1.2rem;
  color: #e0e0e0;
}

.modal-close {
  background: none;
  border: none;
  color: #999;
  font-size: 1.8rem;
  line-height: 1;
  cursor: pointer;
}

.modal-body {
  padding: 1.25rem;
}

.modal-description {
  margin: 0 0 1rem 0;
  color: #cfcfcf;
  font-size: 0.9rem;
}

.form-group {
  margin-bottom: 1rem;
}

.form-group label {
  display: block;
  margin-bottom: 0.5rem;
  color: #e0e0e0;
  font-size: 0.9rem;
}

.color-row {
  display: flex;
  gap: 0.5rem;
}

.color-picker {
  width: 52px;
  height: 40px;
  border: 1px solid #555;
  border-radius: 6px;
  background: #1d1d1d;
  padding: 0.15rem;
}

.color-input {
  flex: 1;
  background-color: #1a1a1a;
  border: 1px solid #444;
  border-radius: 4px;
  color: #e0e0e0;
  padding: 0.65rem;
  font-size: 0.9rem;
  text-transform: uppercase;
}

.color-input:focus {
  outline: none;
  border-color: #4a9eff;
}

.preview-wrapper {
  margin-bottom: 1rem;
}

.preview-label {
  color: #bfbfbf;
  font-size: 0.8rem;
}

.preview-chip {
  margin-top: 0.4rem;
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  border: 1px solid #4a4a4a;
  border-radius: 999px;
  padding: 0.25rem 0.6rem;
  color: #dfdfdf;
  font-size: 0.8rem;
}

.preview-dot {
  width: 14px;
  height: 14px;
  border-radius: 50%;
  border: 1px solid rgba(255, 255, 255, 0.35);
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 0.6rem;
  margin-top: 1rem;
}

.btn-cancel,
.btn-submit {
  border: none;
  border-radius: 4px;
  padding: 0.6rem 1rem;
  cursor: pointer;
  font-size: 0.9rem;
}

.btn-cancel {
  background: #464646;
  color: #e0e0e0;
}

.btn-submit {
  background: #4a9eff;
  color: white;
}

.btn-submit:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
</style>
